import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import Navbar from "../components/Navbar";
import {
  ArrowLeft, Download, Mail, FileText, ShieldCheck, Eye,
  Printer, BookOpen,
} from "lucide-react";

/* ---------- Shared brand palette ---------- */
const BRAND = {
  ink: "#0F1B2D",
  orange: "#FF6A00",
  gold: "#FFC145",
  cream: "#FFFBEB",
  muted: "#475569",
};

/* ================================================================== */
/*  Reusable PDF generator                                             */
/* ================================================================== */
// jsPDF's default helvetica font only supports the WinAnsi/Latin-1 subset.
// Em dashes, smart quotes, the ▸ triangle bullet, and other extended Unicode
// glyphs render as garbage boxes. We normalize every string before it hits
// doc.text() / doc.splitTextToSize(). Latin-1 chars (·, ©, etc.) pass through;
// anything outside Latin-1 gets stripped or substituted.
function pdfSafe(text) {
  if (text == null) return "";
  return String(text)
    .replace(/\u2014/g, " - ")  // — em dash
    .replace(/\u2013/g, "-")    // – en dash
    .replace(/[\u2018\u2019]/g, "'")  // ' ' curly single quotes
    .replace(/[\u201C\u201D]/g, '"')  // " " curly double quotes
    .replace(/\u2026/g, "...")  // … ellipsis
    .replace(/\u25B8/g, ">")    // ▸ black right-pointing small triangle
    .replace(/\u2192/g, "->")   // → right arrow
    .replace(/[^\x00-\xFF]/g, "?"); // any remaining non-Latin-1 → ?
}

// Fetch a public-asset image and return a data URL so jsPDF can embed it.
// Returns null on any error so the PDF still renders without the mascot
// rather than throwing on download.
async function loadImageDataUrl(url) {
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Live-domain substitution helper — replaces `{{APP_URL}}` tokens in doc copy
// with whatever origin the app is currently being served from. That way the
// Admin Playbook / Tester Deep-Dive / Welcome doc always reflect the CURRENT
// deployment URL (preview during dev, production after deploy) without any
// code-edit step on cutover.
function getAppUrl() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return "https://app.resmo.app";
}
function expandAppUrl(text) {
  if (text == null) return "";
  return String(text).replace(/\{\{APP_URL\}\}/g, getAppUrl());
}

async function buildPdf({ title, subtitle, sections, footer, coverTagline, coverImageUrl }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 54;
  const contentW = pageW - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // Pre-load cover mascot (if requested) before we start drawing — addImage
  // is synchronous but needs a data URL, so we resolve the fetch first.
  let mascotDataUrl = null;
  if (coverImageUrl) {
    mascotDataUrl = await loadImageDataUrl(coverImageUrl);
  }

  // Cover header bar — taller (120pt) when we have a mascot so the
  // illustration has breathing room.
  const headerH = mascotDataUrl ? 120 : 90;
  doc.setFillColor(15, 27, 45);
  doc.rect(0, 0, pageW, headerH, "F");
  doc.setFillColor(255, 106, 0);
  doc.rect(0, headerH, pageW, 4, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.text("Resmo.", margin, mascotDataUrl ? 60 : 50);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(255, 193, 69);
  doc.text(
    pdfSafe(coverTagline || "CAREER INTELLIGENCE COMPANION · PRIVATE BETA"),
    margin,
    mascotDataUrl ? 82 : 72
  );

  // Mascot on the right side of the cover header, scaled to fit
  if (mascotDataUrl) {
    try {
      // Top-right corner — 90pt tall character, ~24pt right margin
      const imgH = 90;
      const imgW = 90;
      doc.addImage(
        mascotDataUrl,
        "PNG",
        pageW - margin - imgW,
        (headerH - imgH) / 2,
        imgW,
        imgH,
        undefined,
        "FAST"
      );
    } catch {
      // Image embed failed — silently fall back to text-only header
    }
  }
  y = headerH + 40;

  // Title
  doc.setTextColor(15, 27, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  const titleLines = doc.splitTextToSize(pdfSafe(title), contentW);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 26 + 6;

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(71, 85, 105);
    const sublines = doc.splitTextToSize(pdfSafe(subtitle), contentW);
    doc.text(sublines, margin, y);
    y += sublines.length * 14 + 14;
  }

  // Sections
  sections.forEach((s, i) => {
    ensureSpace(60);
    // Section header bar
    doc.setFillColor(255, 251, 235);
    doc.rect(margin, y - 4, contentW, 26, "F");
    doc.setFillColor(255, 106, 0);
    doc.rect(margin, y - 4, 4, 26, "F");
    doc.setTextColor(15, 27, 45);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(pdfSafe(`${i + 1}. ${s.heading}`), margin + 14, y + 13);
    y += 36;

    s.steps.forEach((step) => {
      ensureSpace(50);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(255, 106, 0);
      doc.text(pdfSafe(`> ${step.title}`), margin, y);
      y += 14;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(15, 27, 45);
      const bodyLines = doc.splitTextToSize(pdfSafe(expandAppUrl(step.body)), contentW - 12);
      bodyLines.forEach((line) => {
        ensureSpace(14);
        doc.text(line, margin + 10, y);
        y += 13;
      });
      y += 8;
    });
    y += 8;
  });

  // Footer band
  ensureSpace(40);
  doc.setDrawColor(15, 27, 45);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageW - margin, y);
  y += 14;
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  const footLines = doc.splitTextToSize(
    pdfSafe(footer || "Resmo · Career Intelligence Companion · Private Beta · Confidential under NDA"),
    contentW
  );
  doc.text(footLines, margin, y);

  return doc;
}

/* ================================================================== */
/*  Admin Playbook — for YOU                                           */
/* ================================================================== */
const ADMIN_PLAYBOOK = [
  {
    heading: "Invite a new tester (Admin)",
    steps: [
      {
        title: "Step 1 — Open the admin panel",
        body:
          "Go to {{APP_URL}}. If the gate is up, enter your admin email (tester@resmo.app) + admin code (RESMO-GKNRZP8E) → Unlock. Click LOGIN → sign in with tester@resmo.app / TestResmo123!. Navigate to /admin/beta.",
      },
      {
        title: "Step 2 — Generate the invite code",
        body:
          "Use the yellow Quick Add (bulk) panel. Paste the tester's email (one per line if multiple). Pick Max uses: 'Unlimited' for trusted long-term testers, '1' for one-shot demos that auto-revoke after first unlock. Click 'Generate all'. A green results card appears with their unique RESMO-XXXXXXXX code.",
      },
      {
        title: "Step 3 — Copy the ready-to-send message",
        body:
          "On the green results card, click 'Copy all N messages'. The full invite letter (URL + email + code + NDA reminder) is now on your clipboard, ready to paste into your email client.",
      },
      {
        title: "Step 4 — Verify before sending",
        body:
          "Scroll down to the invite list. Find their row. Confirm: Status badge says 'ACTIVE' (green), Uses = 0, Email matches exactly what you typed. If anything looks off, click the trash icon to delete and start over.",
      },
    ],
  },
  {
    heading: "Send the tester the letter (Email)",
    steps: [
      {
        title: "Step 5 — Compose your email",
        body:
          "Open Gmail / Outlook / your email client. To: the tester's email. Subject: 'You're in — your Resmo private beta invite'. Paste the message from your clipboard. Personalize the greeting and signature. Send.",
      },
      {
        title: "Step 6 — Confirm delivery & track",
        body:
          "Look for the 'Sent' confirmation. Most testers redeem within 24 hours. Back in /admin/beta, refresh — their row's 'Last used' will update the moment they unlock the gate.",
      },
    ],
  },
  {
    heading: "Monitor for misuse (Daily)",
    steps: [
      {
        title: "Watch the usage telemetry",
        body:
          "Each invite row shows Uses, Created, Redeemed, Last used, and IP. Signals to act on: same code with multiple IPs in a short window, unexpected geography on an IP, a code that suddenly spikes in use_count, or a use_count = 0 after a week (nudge them).",
      },
      {
        title: "Revoke a leaked code instantly",
        body:
          "Find the row in /admin/beta → click 'Revoke'. The legitimate user's existing 30-day cookie keeps working, but any new unlock attempts (from a sharer) are blocked immediately. You can 'Restore' later if needed.",
      },
      {
        title: "Single-use auto-revoke",
        body:
          "When you create an invite with Max Uses = 1, it self-destructs after first unlock. Status flips to 'EXHAUSTED' (red badge). Useful for cold leads, press demos, or screensharing-only handoffs.",
      },
    ],
  },
  {
    heading: "When NDAs come back for new testers",
    steps: [
      {
        title: "Repeat the flow — ~10-20 seconds per tester",
        body:
          "Open /admin/beta → Quick Add → paste email(s) → Generate all → 'Copy all messages' → paste into email → send. The flow is intentionally fast so you can onboard waves of testers in one session.",
      },
    ],
  },
  {
    heading: "Common tester support",
    steps: [
      {
        title: "'It says invalid code or email'",
        body:
          "Compare what you sent vs what they typed. Codes are case-insensitive but the email must match. If they typed an alias (e.g., gmail+tag@), tell them to use the exact form you sent.",
      },
      {
        title: "'It says my email doesn't match the invite'",
        body:
          "They're trying to REGISTER with a different email than the code is bound to. Their gate cookie is from one email, their register form has another. Tell them to use the same email as the invite.",
      },
      {
        title: "'I lost my code'",
        body:
          "Find their row in /admin/beta → click 'Code' button → re-send via email. No regeneration needed; their code is still valid.",
      },
    ],
  },
];

const ADMIN_FOOTER = "Admin playbook · Generated from /admin/beta/playbook · For your eyes only. Do not share.";

/* ================================================================== */
/*  Tester Welcome Guide — for the TESTERS                             */
/*  Distribution-ready. Two clear parts:                                */
/*     PART A — How to access Resmo (the unlock + sign-up)              */
/*     PART B — What to expect inside (every tool, latest features)     */
/* ================================================================== */
const TESTER_GUIDE = [
  {
    heading: "PART A · HOW TO ACCESS RESMO",
    steps: [
      {
        title: "Step 1 — Open the beta URL",
        body:
          "In your browser, go to: {{APP_URL}}. You'll see a dark 'Private Beta · Invitation Required' screen with a small unlock form. Bookmark this URL — it's how you'll come back every time.",
      },
      {
        title: "Step 2 — Unlock the gate",
        body:
          "Type the SAME email address the invite was sent to. Paste your RESMO-XXXXXXXX code from the email (format: RESMO- followed by 8 characters). Click Unlock. You'll see a quick 'Welcome in' celebration and then land on the Resmo home page.",
      },
      {
        title: "Step 3 — Create your account",
        body:
          "Click 'Sign up' in the top right. Use the SAME email as the invite — registration is locked to that exact address (this is intentional, for traceability). Pick any password (8+ chars). You're in. From now on, opening the URL just asks for your password, not the code.",
      },
      {
        title: "Step 4 — Pick your voice: Resmo or ProMo",
        body:
          "Resmo (Entry · Mid-career) is playful, mascot-led, and warm. ProMo (Seasoned · Senior) is data-led, restrained, and no-cartoon. Pick one on the landing page or let the Smart Path recommender suggest one based on your resume. You can switch any time from Account → Mode.",
      },
      {
        title: "Step 5 — Drop your resume once",
        body:
          "From the landing page, click 'Add your resume' or 'My Resume' in the navbar. Upload a PDF / DOCX / paste text. This becomes your 'base resume' — every tool in the app uses it. You can replace it any time.",
      },
    ],
  },
  {
    heading: "PART B · WHAT TO EXPECT INSIDE — Core Tools",
    steps: [
      {
        title: "Tailor a Resume",
        body:
          "Paste a job description (or upload one) and Resmo produces: a Fit Score, an Alignment Matrix (skills you knock out vs gaps), the Problem Statement (what the role is REALLY hiring for), Company Tone insights, and a Tailored Resume you can download as PDF or DOCX. Saved to History.",
      },
      {
        title: "Skill Lab — what you're worth",
        body:
          "Rate your skills 1-5, pick a target role, get a salary range, top-value skills, negotiation talking points, AND adjacent roles your skills also fit (with fit scores and salary ranges). Each adjacent role has a 'Research this role →' button that warps you into Role Research.",
      },
      {
        title: "Research a Role",
        body:
          "Curious about a role you've never held? Run a research brief: salary tiers (Entry → Principal), must-have / nice-to-have skills, career path, education resources, and market outlook. Shareable as a public read-only link.",
      },
      {
        title: "Job Search (live · Adzuna-powered)",
        body:
          "Type a role + location and Resmo serves live job listings from Adzuna (15 at a time) alongside curated search links for LinkedIn, Indeed, Glassdoor, Wellfound, Built In, Google Jobs, ZipRecruiter, and HN Who's Hiring. Country switchable (US / UK / CA / AU / DE / FR / IN).",
      },
      {
        title: "Interview Prep",
        body:
          "Auto-generate a tailored interview question bank for any role + level (Entry through VP). 20-26 questions across Behavioral / Technical / Situational / Leadership categories, plus red-flag traps and 5-7 questions YOU should ask the interviewer. Downloadable as PDF.",
      },
      {
        title: "Mock Interview (practice room — NEW)",
        body:
          "Live Q&A loop. Pick 3, 5, or 7 questions; type or record your answer (we transcribe via Whisper). After each answer you get: a 4-point rubric (Clarity · Structure · Relevance · Confidence, each 0-5), a coach note, a 90-120 word 'stronger answer' example in your voice, and a follow-up probe a real interviewer would ask. End with an overall scorecard (0-100), 3 strengths, 3 fixes, next-step coaching. Tone matches your mode (Resmo warm / ProMo terse).",
      },
      {
        title: "Stepping Stones (interview journal)",
        body:
          "Every interview teaches you something — even rejections. Log wins, focus areas for next time, interviewer feedback, contacts to stay in touch with, and notes. Your growth log on the way to the dream role. Mock Interview scorecards can be saved here with one click.",
      },
      {
        title: "Tips & Resources Hub",
        body:
          "Live job-market news feed (auto-refreshing every 5 min), curated career tips, and quick links to every tool. ProMo mode adds a ticker strip at the top of the Tailor page for industry pulse.",
      },
    ],
  },
  {
    heading: "PART B · WHAT TO EXPECT INSIDE — Smart shortcuts",
    steps: [
      {
        title: "One-click 'Tailor this job' from live listings",
        body:
          "Every Adzuna listing has a 'Tailor my resume to this job →' button. Clicking it jumps you to the Tailor flow with title / company / description pre-filled. A 3-second auto-tailor countdown kicks off automatically — cancel any time to edit before submitting.",
      },
      {
        title: "Practice your saved interview banks",
        body:
          "On any saved Interview Prep result, click the teal 'Run a mock interview →' button. It pulls the questions straight into the Mock Interview practice room (no re-typing).",
      },
      {
        title: "Share a Role Research brief",
        body:
          "Inside any role research, click Share. You get a public read-only link you can send to a manager, mentor, or partner — no login required on their end.",
      },
      {
        title: "History + side-by-side compare",
        body:
          "Every analysis is saved. Open two from History and compare them side-by-side to see how your tailored resume changed between roles.",
      },
    ],
  },
  {
    heading: "How to give us feedback",
    steps: [
      {
        title: "Reply to the invite email",
        body:
          "Anything that's broken, confusing, or magical — please reply directly to the email you got your invite in. Screenshots help! We read every one and prioritize fixes weekly.",
      },
      {
        title: "Sharp bug reports help us move fast",
        body:
          "If something errors, tell us: (1) which page you were on, (2) what you clicked, (3) what you expected to happen, (4) what actually happened. The fine-print watermark at the bottom-left of every page shows your email + timestamp — copy that into your report so we can find your session instantly.",
      },
    ],
  },
  {
    heading: "Ground rules — please read",
    steps: [
      {
        title: "This is a private beta — keep it private",
        body:
          "Please don't share the URL, your invite code, or screenshots outside your immediate circle. Per the NDA you signed and the watermark on every page, all access is logged. If a code is shared, we'll see it in the telemetry and revoke immediately.",
      },
      {
        title: "We can't guarantee outcomes",
        body:
          "Resmo is a powerful career-intelligence companion, but we don't guarantee employment, interviews, offers, or specific salary outcomes. Outputs may contain errors or be out of date. Always review before sending anything to an employer. Resmo is not a substitute for professional legal, financial, recruiting, or career-counseling advice.",
      },
      {
        title: "Your data stays yours",
        body:
          "Your resume, analyses, salary data, mock-interview transcripts, and journal entries are private to your account. The only thing that gets a public link is a Role Research brief — and only when YOU click 'Share'.",
      },
    ],
  },
];

const TESTER_FOOTER =
  "Welcome to the Resmo private beta. Reply to your invite email anytime with feedback. — The Resmo Team";

/* ================================================================== */
/*  Beta Welcome Letter — the hero document admins email/attach        */
/*  with each invite. Punchy, mascot-led intro to "the Resume Monster" */
/*  + a 4-step quick-start. Designed to fit on ~2 printed pages.       */
/* ================================================================== */
const BETA_WELCOME = [
  {
    heading: "Meet Resmo — your Resume Monster",
    steps: [
      {
        title: "What Resmo is",
        body:
          "Resmo is a career-intelligence companion that eats job descriptions for breakfast and feeds you back a tailored resume, a fit score, a role strategy brief, salary intel, mock interviews, and a journal of every interview you've ever taken. Think of Resmo as the friendly monster in your corner — pen behind the ear, calculator in one paw, espresso in the other — built to do the grunt work so you can show up to interviews ready, not exhausted.",
      },
      {
        title: "Why we built it",
        body:
          "Job hunting is broken. People copy-paste keywords hoping to beat ATS bots, scroll through 200 listings a night, and walk into interviews under-prepared for the real problem the role was opened to solve. Resmo flips the script: it reads the JD like a hiring manager would, lines up the candidate's actual story against it, and rehearses you out loud — with a coach in the room.",
      },
      {
        title: "Why you're getting this letter",
        body:
          "You're a founding tester. That's not a marketing label — your feedback literally shapes V1. The bugs you find, the language that confused you, the magic moments that surprised you: all of it lands on the team's whiteboard this week. The seal at the bottom of every tailored resume says 'Verified by Resmo'. The seal under this beta says 'Shaped by you'.",
      },
      {
        title: "What founding testers get",
        body:
          "Free access through public launch. Direct line to the founder (reply to any Resmo email — it lands in the inbox). Your name on the credits page (opt-in). First crack at every new tool the day it ships. And the warm, slightly-feral satisfaction of telling future Resmo users 'I was there first.'",
      },
    ],
  },
  {
    heading: "Your first 10 minutes — quick start",
    steps: [
      {
        title: "Step 1 — Unlock the gate (60 seconds)",
        body:
          "Open {{APP_URL}}. You'll see a dark 'Private Beta · Invitation Required' screen. Paste the SAME email this letter was sent to, paste your RESMO-XXXXXXXX code, click Unlock. The monster purrs. You're in.",
      },
      {
        title: "Step 2 — Create your account (30 seconds)",
        body:
          "Click 'Sign up' in the navbar. Use the SAME email as the invite — registration is locked to that exact address (this is intentional, for traceability). Pick any 8+ char password. From now on, opening the URL just asks for your password, not the code.",
      },
      {
        title: "Step 3 — Feed Resmo your resume (2 minutes)",
        body:
          "Click 'Add your resume' on the landing page. Drop a PDF, DOCX, or paste text. This becomes your 'base resume' — every tool in the app feeds off it. The monster digests it on the spot: extracts skills, experience years, business currency, and the story arc of your career.",
      },
      {
        title: "Step 4 — Tailor your first resume (60 seconds)",
        body:
          "Click 'Tailor a Resume', paste any job description. Resmo returns: a Fit Score (0-100), the Problem Statement (what the role is REALLY hiring for), an Alignment Matrix (what you knock out vs gaps), Company Tone insights, and a tailored resume you can download as PDF or DOCX. That's the loop. Repeat for every role you care about.",
      },
    ],
  },
  {
    heading: "Then explore the rest — the full toolkit",
    steps: [
      {
        title: "Skill Lab",
        body:
          "Rate your skills 1-5, pick a target role, get a salary range + adjacent roles your skills also fit (with fit scores and salary ranges). Each adjacent role has a one-click 'Research this role →' jump.",
      },
      {
        title: "Role Research",
        body:
          "Curious about a role you've never held? Run a research brief: salary tiers, must-have / nice-to-have skills, career path, education resources, and market outlook. Shareable as a public read-only link.",
      },
      {
        title: "Interview Prep + Mock Interview",
        body:
          "Auto-generate a tailored question bank (20-26 questions across Behavioral / Technical / Situational / Leadership + traps + questions YOU should ask). Then step into the practice room: type or record your answer, get a 4-point rubric scored 0-5, a coach note, and a 'stronger answer' example written in your own voice.",
      },
      {
        title: "Career Crew + Stepping Stones",
        body:
          "Career Crew is your network journal — every contact, what they care about, last touch, warm-intro ideas. Stepping Stones is your interview growth log — every interview you take, what you learned, what to fix next time. Every Mock Interview scorecard one-clicks into your Stones.",
      },
      {
        title: "Honesty Check",
        body:
          "Every tailored claim is audited against your original resume. Expansions and fabrications get flagged with a yellow ribbon for you to approve, edit, or revert. The Verified by Resmo seal on the final PDF says 'every claim here is anchored to the candidate's real story.'",
      },
    ],
  },
  {
    heading: "How to give us feedback (yes, please)",
    steps: [
      {
        title: "Reply to your invite email",
        body:
          "Anything broken, confusing, or magical — reply directly to the email you got your invite in. Screenshots help. We read every one and ship fixes weekly.",
      },
      {
        title: "What makes a sharp bug report",
        body:
          "Tell us: (1) which page, (2) what you clicked, (3) what you expected, (4) what actually happened. The fine-print watermark at the bottom-left of every page shows your email + timestamp — copy that into your report so we can find your session instantly.",
      },
      {
        title: "Ground rules — please read",
        body:
          "Private beta — please don't share the URL, your invite code, or screenshots outside your immediate circle. Every page is watermarked with your email + timestamp; all access is logged. Resmo can't guarantee outcomes — outputs may have errors, always review before sending to an employer. Your data stays private to your account; nothing is shared without your explicit click.",
      },
    ],
  },
];

const BETA_WELCOME_FOOTER =
  "Resmo · The Resume Monster · Private beta · Confidential under NDA · Reply to your invite email anytime — The Resmo Team";

/* ================================================================== */
/*  Reusable on-screen renderer                                        */
/* ================================================================== */
function PlaybookRenderer({ kind, title, subtitle, sections, footer, coverTagline, coverImageUrl }) {
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const onDownloadPdf = async () => {
    const doc = await buildPdf({ title, subtitle, sections, footer, coverTagline, coverImageUrl });
    doc.save(`resmo-${kind}.pdf`);
  };

  const onPrint = () => window.print();

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <button
          onClick={() => navigate("/admin/beta")}
          className="rm-tag mb-4"
          data-testid={`${kind}-back`}
        >
          <ArrowLeft size={14} /> Back to /admin/beta
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div className="flex-1 min-w-[260px]">
            <div className="rm-tag mb-3">
              {kind === "admin-playbook" ? (
                <><ShieldCheck size={12} /> Admin only</>
              ) : kind === "beta-welcome" ? (
                <><Mail size={12} /> Welcome letter · For testers</>
              ) : (
                <><BookOpen size={12} /> For your testers</>
              )}
            </div>
            <h1
              className="font-display uppercase leading-none"
              style={{
                fontSize: "clamp(2.2rem, 5vw, 3.5rem)",
                letterSpacing: "-0.04em",
                color: BRAND.ink,
              }}
            >
              {title.split(" ").map((w, i, arr) => {
                const isLast = i === arr.length - 1;
                return (
                  <React.Fragment key={i}>
                    {isLast ? <span className="rm-text-orange">{w}</span> : w}
                    {!isLast && " "}
                  </React.Fragment>
                );
              })}
            </h1>
            {subtitle && (
              <p className="mt-3 text-base md:text-lg max-w-2xl"
                 style={{ color: BRAND.muted }}>
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={onDownloadPdf}
              className="rm-btn rm-btn-primary"
              data-testid={`${kind}-download-pdf`}
            >
              <Download size={16} /> Download PDF
            </button>
            <button
              onClick={onPrint}
              className="rm-btn rm-btn-ghost text-sm"
              data-testid={`${kind}-print`}
            >
              <Printer size={14} /> Print
            </button>
          </div>
        </div>

        <hr className="my-8" style={{ borderColor: "rgba(15,27,45,0.15)" }} />

        <div ref={contentRef} className="space-y-10" data-testid={`${kind}-content`}>
          {kind === "beta-welcome" && (
            <div
              className="rm-card-static p-6 md:p-10 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #0F1B2D 0%, #1F3559 55%, #FF6A00 140%)",
                border: `3px solid ${BRAND.ink}`,
                color: "white",
              }}
              data-testid="beta-welcome-hero"
            >
              <div
                aria-hidden
                className="absolute -top-10 -right-10 w-48 h-48 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,193,69,0.35) 0%, transparent 70%)",
                }}
              />
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center relative">
                <div>
                  <div
                    className="font-mono-rm text-[10px] uppercase tracking-widest mb-3"
                    style={{ color: BRAND.gold }}
                  >
                    Founding tester · Private beta
                  </div>
                  <h2
                    className="font-display uppercase leading-none mb-3"
                    style={{
                      fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                      letterSpacing: "-0.03em",
                      color: "white",
                    }}
                  >
                    Resmo is your{" "}
                    <span style={{ color: BRAND.orange }}>Resume Monster</span>.
                  </h2>
                  <p
                    className="text-sm md:text-base leading-relaxed max-w-xl"
                    style={{ color: "#E2E8F0" }}
                  >
                    Friendly in the navbar, ferocious on your behalf — Resmo
                    eats job descriptions, digests them against your real story,
                    and impresses recruiters with a tailored resume, a coach-led
                    mock interview, and a journal of every step you take toward
                    the role you actually want. This letter is your two-minute
                    start. Read it, run the four-step quick-start, then make a
                    mess.
                  </p>
                  <div className="flex items-center gap-3 mt-5 flex-wrap">
                    <span
                      className="rm-tag"
                      style={{
                        background: "rgba(255,255,255,0.12)",
                        color: "white",
                        borderColor: "rgba(255,193,69,0.5)",
                      }}
                      data-testid="beta-welcome-hero-tag-eat"
                    >
                      EAT the JD
                    </span>
                    <span
                      className="rm-tag"
                      style={{
                        background: "rgba(255,255,255,0.12)",
                        color: "white",
                        borderColor: "rgba(255,193,69,0.5)",
                      }}
                      data-testid="beta-welcome-hero-tag-digest"
                    >
                      DIGEST your story
                    </span>
                    <span
                      className="rm-tag"
                      style={{
                        background: "rgba(255,255,255,0.12)",
                        color: "white",
                        borderColor: "rgba(255,193,69,0.5)",
                      }}
                      data-testid="beta-welcome-hero-tag-impress"
                    >
                      IMPRESS the room
                    </span>
                  </div>
                </div>
                <img
                  src="/brand/resmo-welcoming.png"
                  alt="Resmo, the friendly Resume Monster, waving hello"
                  className="hidden md:block w-44 lg:w-56 h-auto drop-shadow-2xl"
                  data-testid="beta-welcome-hero-mascot"
                  loading="eager"
                  style={{ filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.4))" }}
                />
              </div>
            </div>
          )}

          {sections.map((s, i) => (
            <section key={i}>
              <div
                className="rm-card-static p-5 md:p-6 mb-4"
                style={{ background: BRAND.cream }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center font-display"
                    style={{
                      background: BRAND.orange,
                      color: "white",
                      border: `3px solid ${BRAND.ink}`,
                    }}
                  >
                    {i + 1}
                  </div>
                  <h2 className="font-display text-xl md:text-2xl uppercase leading-tight">
                    {s.heading}
                  </h2>
                </div>
              </div>

              <ol className="space-y-4 pl-2">
                {s.steps.map((step, j) => (
                  <li
                    key={j}
                    className="rm-card p-5"
                    data-testid={`${kind}-step-${i}-${j}`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="font-display text-lg rm-text-orange flex-shrink-0"
                        aria-hidden
                      >
                        ▸
                      </span>
                      <div>
                        <h3 className="font-display text-base uppercase mb-1.5"
                            style={{ color: BRAND.ink }}>
                          {step.title}
                        </h3>
                        <p className="text-sm md:text-[15px] leading-relaxed"
                           style={{ color: BRAND.muted }}>
                          {expandAppUrl(step.body)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>

        <hr className="my-10" style={{ borderColor: "rgba(15,27,45,0.15)" }} />

        <div className="font-mono-rm text-xs uppercase tracking-widest text-center"
             style={{ color: BRAND.muted }}>
          {footer}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Exports                                                            */
/* ================================================================== */
export function AdminPlaybookPage() {
  return (
    <PlaybookRenderer
      kind="admin-playbook"
      title="Beta launch playbook"
      subtitle="Your runbook for inviting testers, sending them the letter, and managing access during the private beta. Print it, save the PDF, or keep this tab open."
      sections={ADMIN_PLAYBOOK}
      footer={ADMIN_FOOTER}
    />
  );
}

export function TesterGuidePage() {
  return (
    <PlaybookRenderer
      kind="tester-guide"
      title="Welcome to the beta"
      subtitle="A quick step-by-step on how to unlock the beta, set up your account, and use Resmo's tools. Attach this to your invite emails so testers know what to expect."
      sections={TESTER_GUIDE}
      footer={TESTER_FOOTER}
      coverImageUrl="/brand/resmo-welcoming.png"
    />
  );
}

export function BetaWelcomePage() {
  return (
    <PlaybookRenderer
      kind="beta-welcome"
      title="Meet Resmo — your Resume Monster"
      subtitle="Your founding-tester welcome letter. Punchier than the full guide — designed to print or PDF on two pages and ride along with the invite email. Hand this to every new tester."
      sections={BETA_WELCOME}
      footer={BETA_WELCOME_FOOTER}
      coverTagline="THE RESUME MONSTER · PRIVATE BETA · FOUNDING TESTERS"
      coverImageUrl="/brand/resmo-welcoming.png"
    />
  );
}

export default function BetaDocs({ kind }) {
  if (kind === "tester-guide") return <TesterGuidePage />;
  if (kind === "beta-welcome") return <BetaWelcomePage />;
  return <AdminPlaybookPage />;
}

/* ================================================================== */
/*  Tiny link tile component used on /admin/beta to advertise these    */
/* ================================================================== */
export function BetaDocsCallouts() {
  const navigate = useNavigate();
  return (
    <div
      className="rm-card-static p-5 md:p-6 mb-6"
      style={{ background: "white", border: `3px solid ${BRAND.ink}` }}
      data-testid="beta-docs-callouts"
    >
      <div className="flex items-center gap-2 mb-3">
        <FileText size={16} className="rm-text-orange" />
        <div className="rm-label">Beta launch docs</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          onClick={() => navigate("/admin/beta/playbook")}
          className="text-left p-4 rounded-md transition-transform hover:-translate-y-0.5"
          style={{
            background: BRAND.cream,
            border: `3px solid ${BRAND.ink}`,
            boxShadow: `4px 4px 0 0 ${BRAND.ink}`,
          }}
          data-testid="link-admin-playbook"
        >
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={14} className="rm-text-orange" />
            <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                  style={{ color: BRAND.muted }}>
              Admin only
            </span>
          </div>
          <h3 className="font-display text-lg uppercase leading-tight">
            Admin <span className="rm-text-orange">playbook</span>
          </h3>
          <p className="text-xs mt-1.5" style={{ color: BRAND.muted }}>
            Step-by-step: invite a tester, send the letter, monitor for misuse, revoke leaks.
          </p>
        </button>

        <button
          onClick={() => navigate("/admin/beta/welcome")}
          className="text-left p-4 rounded-md transition-transform hover:-translate-y-0.5 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #0F1B2D 0%, #1F3559 55%, #FF6A00 140%)",
            color: "white",
            border: `3px solid ${BRAND.ink}`,
            boxShadow: `4px 4px 0 0 ${BRAND.orange}`,
          }}
          data-testid="link-beta-welcome"
        >
          <div className="flex items-center gap-2 mb-1">
            <Mail size={14} style={{ color: BRAND.gold }} />
            <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                  style={{ color: BRAND.gold }}>
              New · For testers
            </span>
          </div>
          <h3 className="font-display text-lg uppercase leading-tight" style={{ color: "white" }}>
            Beta <span style={{ color: BRAND.orange }}>welcome</span> letter
          </h3>
          <p className="text-xs mt-1.5" style={{ color: "#CBD5E1" }}>
            &ldquo;Meet Resmo, the Resume Monster&rdquo; — 2-page hero doc with quick-start. Email it with every invite.
          </p>
        </button>

        <button
          onClick={() => navigate("/admin/beta/tester-guide")}
          className="text-left p-4 rounded-md transition-transform hover:-translate-y-0.5"
          style={{
            background: "#0F1B2D",
            color: "white",
            border: `3px solid ${BRAND.ink}`,
            boxShadow: `4px 4px 0 0 ${BRAND.gold}`,
          }}
          data-testid="link-tester-guide"
        >
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={14} style={{ color: BRAND.gold }} />
            <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                  style={{ color: BRAND.gold }}>
              Full guide · For testers
            </span>
          </div>
          <h3 className="font-display text-lg uppercase leading-tight" style={{ color: "white" }}>
            Tester <span className="rm-text-orange">deep-dive</span>
          </h3>
          <p className="text-xs mt-1.5" style={{ color: "#CBD5E1" }}>
            The long-form manual — every tool, every shortcut, ground rules. For testers who want it all.
          </p>
        </button>
      </div>
      <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-3"
           style={{ color: BRAND.muted }}>
        <Eye size={10} className="inline" /> All three pages include a Download PDF button.
      </div>
    </div>
  );
}
